import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, X, ChevronRight, Home, Loader, Folder, File, CheckCircle, Image } from 'lucide-react';
import { getDownloadToken } from '../../services/api';

// Modale de sélection d'une image parmi les documents du cabinet (pour la signature email).
const SignatureDocumentPicker = ({
  showDocumentPicker,
  setShowDocumentPicker,
  folderBreadcrumb,
  navigateToBreadcrumb,
  navigateToFolder,
  selectDocument,
  loadingDocs,
  cabinetFolders,
  cabinetDocuments,
  backendUrl
}) => {
  const { t } = useTranslation('admin');
  // S3 : token de téléchargement court pour les vignettes (récupéré à l'ouverture)
  const [previewToken, setPreviewToken] = useState(null);
  useEffect(() => {
    if (!showDocumentPicker) { setPreviewToken(null); return; }
    let cancelled = false;
    (async () => { const t = await getDownloadToken(); if (!cancelled) setPreviewToken(t); })();
    return () => { cancelled = true; };
  }, [showDocumentPicker]);

  if (!showDocumentPicker) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-blue-50">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center">
              <FolderOpen className="mr-2 text-cyan-600" size={20} />
              {t('docs.picker.selectImage')}
            </h3>
            <p className="text-sm text-slate-600">{t('docs.picker.cabinetDocs')}</p>
          </div>
          <button
            onClick={() => setShowDocumentPicker(false)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center space-x-1 text-sm overflow-x-auto">
          {folderBreadcrumb.map((folder, index) => (
            <React.Fragment key={folder.id || 'root'}>
              {index > 0 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className={`flex items-center px-2 py-1 rounded hover:bg-slate-200 flex-shrink-0 ${
                  index === folderBreadcrumb.length - 1 ? 'font-medium text-cyan-600' : 'text-slate-600'
                }`}
              >
                {index === 0 ? <Home size={14} className="mr-1" /> : null}
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingDocs ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-cyan-600" size={32} />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Dossiers */}
              {cabinetFolders.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2 uppercase font-medium">{t('docs.browser.folders')}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {cabinetFolders.map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => navigateToFolder(folder)}
                        className="flex items-center p-3 bg-slate-50 hover:bg-cyan-50 rounded-lg border border-slate-200 hover:border-cyan-200 transition-colors text-left"
                      >
                        <Folder className="text-amber-500 mr-2 flex-shrink-0" size={20} />
                        <span className="text-sm text-slate-700 truncate">{folder.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Images */}
              {cabinetDocuments.length > 0 ? (
                <div>
                  <p className="text-xs text-slate-500 mb-2 uppercase font-medium">{t('docs.picker.availableImages')}</p>
                  <div className="grid grid-cols-4 gap-3">
                    {cabinetDocuments.map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => selectDocument(doc)}
                        className="group relative aspect-square bg-slate-100 rounded-lg border-2 border-slate-200 hover:border-cyan-500 overflow-hidden transition-all"
                      >
                        <img
                          src={`${backendUrl}/api/admin/cabinet/documents/${doc.id}/preview?token=${previewToken}`}
                          alt={doc.original_filename}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="hidden items-center justify-center w-full h-full">
                          <File className="text-slate-400" size={32} />
                        </div>
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                          <CheckCircle className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                          <p className="text-xs text-white truncate">{doc.original_filename}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : cabinetFolders.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Image className="mx-auto mb-3 text-slate-300" size={48} />
                  <p>{t('docs.picker.noImages')}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {t('docs.picker.uploadHint')}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={() => setShowDocumentPicker(false)}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureDocumentPicker;
